<template>
    <DropDown v-if="hasVisibleItems" v-model:expand="expandDropdown" dropdown-class="mt-1 w-60">
        <template #dropdown-content>
            <div v-for="group of visibleGroups" :key="group.key" class="py-1">
                <div class="px-6 pt-2 pb-1 text-xs uppercase tracking-wide text-gray-500" role="presentation">
                    {{ group.label }}
                </div>
                <button
                    v-for="item of group.items"
                    :key="item.key"
                    class="text-left w-full px-6 py-2 hover:bg-gray-100 cursor-pointer"
                    @click="onItemClicked(item)"
                    @keydown.esc="expandDropdown = false"
                >
                    <div class="py-2 font-medium" role="menuitem">
                        <div style="grid-template-columns: 2em auto" class="grid gap-2">
                            <div class="grid content-center">
                                <Icon :src="item.icon"></Icon>
                            </div>
                            <span class="h-full text-gray-800 inline-flex items-center">{{ item.label }}</span>
                        </div>
                    </div>
                </button>
            </div>
        </template>

        <button
            type="button"
            class="h-full grid content-center cursor-pointer"
            :aria-label="$t('layouts.dashboard.topBar.gearMenu.header')"
            @click="expandDropdown = !expandDropdown"
        >
            <Icon :src="gearIcon" class="filter brightness-0 invert w-7 h-7"></Icon>
        </button>
    </DropDown>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DropDown from '@/components/common/DropDown.vue';
import Icon from '@/components/icons/Icon.vue';
import { store } from '@/plugins/store';
import { IconSource } from '@/types';

type GearMenuItem = {
    key: string;
    icon: IconSource;
    label: string;
    route: string;
};

type GearMenuGroup = {
    key: string;
    label: string;
    items: GearMenuItem[];
};

export default defineComponent({
    components: { DropDown, Icon },
    setup() {
        return {
            gearIcon: IconSource.NavSettings,
        };
    },
    data() {
        return {
            expandDropdown: false,
        };
    },
    computed: {
        canManageDirectory(): boolean {
            return store.getters.loggedInUserCanManageDirectory;
        },
        cookieBotConfigured(): boolean {
            return process.env.VUE_APP_COOKIE_BOT_DOMAIN_ID !== undefined;
        },
        administrationItems(): GearMenuItem[] {
            if (!this.canManageDirectory) {
                return [];
            }
            return [
                {
                    key: 'users',
                    icon: IconSource.UserManagement,
                    label: this.$t('layouts.dashboard.topBar.settings.users'),
                    route: '/settings/users',
                },
                {
                    key: 'locations',
                    icon: IconSource.Farms,
                    label: this.$t('layouts.dashboard.topBar.settings.locations'),
                    route: '/settings/locations',
                },
                {
                    key: 'setup',
                    icon: IconSource.NavSettings,
                    label: this.$t('layouts.dashboard.topBar.settings.setup'),
                    route: '/settings/setup',
                },
            ];
        },
        systemItems(): GearMenuItem[] {
            if (!this.cookieBotConfigured) {
                return [];
            }
            return [
                {
                    key: 'cookies',
                    icon: IconSource.Cookie,
                    label: this.$t('layouts.dashboard.topBar.settings.cookies'),
                    route: '/settings/cookies',
                },
            ];
        },
        visibleGroups(): GearMenuGroup[] {
            const groups: GearMenuGroup[] = [
                {
                    key: 'administration',
                    label: this.$t('layouts.dashboard.topBar.gearMenu.administration'),
                    items: this.administrationItems,
                },
                {
                    key: 'system',
                    label: this.$t('layouts.dashboard.topBar.gearMenu.system'),
                    items: this.systemItems,
                },
            ];
            return groups.filter((group) => group.items.length > 0);
        },
        hasVisibleItems(): boolean {
            return this.visibleGroups.length > 0;
        },
    },
    methods: {
        onItemClicked(item: GearMenuItem) {
            this.$router.push(item.route);
            this.expandDropdown = false;
        },
    },
});
</script>
